/**
 * RegionalGuidesShowcase — home-page section spotlighting the 6 region
 * groups of programmatic-SEO regional guides.
 *
 * Purpose:
 *   1. Human discovery — new visitors landing on `/` see a curated
 *      "browse by region" section leading into `/wine-country-guides`
 *      and directly into each detail guide.
 *   2. SEO — internal links from the homepage flow PageRank into the
 *      programmatic landing pages, boosting their rank for long-tail
 *      queries.
 *
 * Reads GET /api/regional-guides (published guides, same as the hub page
 * and the sitemap), falling back to the shipped /regional-guides.json when
 * the API is unreachable (lib/guidesSource).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight } from '@phosphor-icons/react';
import { loadRegionalGuides } from '@/lib/guidesSource';

const REGION_ORDER = [
  'Napa Valley',
  'Sonoma County',
  'Willamette Valley',
  'Paso Robles',
  'Santa Ynez Valley',
  'Finger Lakes',
];

const RegionalGuidesShowcase = () => {
  const [guides, setGuides] = useState([]);

  useEffect(() => {
    // graceful degradation — with no guides the section just won't render
    loadRegionalGuides().then(setGuides).catch(() => {});
  }, []);

  const groups = useMemo(() => {
    const byRegion = {};
    for (const g of guides) {
      if (!byRegion[g.region]) byRegion[g.region] = [];
      byRegion[g.region].push(g);
    }
    return REGION_ORDER
      .map((r) => [r, byRegion[r] || []])
      .filter(([, arr]) => arr.length > 0);
  }, [guides]);

  if (groups.length === 0) return null;

  return (
    <section
      className="py-16 md:py-24 bg-gradient-to-b from-white via-wine-50/30 to-white"
      data-testid="home-regional-guides-showcase"
    >
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-10 md:mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-4"
        >
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-wine-600 mb-3">
              Curated collections
            </div>
            <h2 className="text-3xl md:text-4xl font-serif tracking-tight text-gray-900">
              Browse wine-country guides by region
            </h2>
            <p className="mt-3 text-base text-gray-600 max-w-2xl">
              Hand-picked itinerary collections for every trip style — dog-friendly,
              romantic, budget, weekend, first-timer, and more. Each guide has 12
              community-voted 3-stop day trips ready to book.
            </p>
          </div>
          <Link
            to="/wine-country-guides"
            className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-wine-700 hover:text-wine-800 transition-colors whitespace-nowrap"
            data-testid="home-guides-view-all-link"
          >
            View all guides
            <ArrowRight size={16} weight="bold" />
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {groups.map(([region, regionGuides], i) => {
            const first = regionGuides[0];
            return (
              <motion.div
                key={region}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <Link
                  to={`/wineries/${first.regionSlug}/best-${first.intentSlug}-itineraries`}
                  className="group block h-full rounded-2xl border border-gray-200 bg-white p-6 hover:border-wine-400 hover:shadow-md transition"
                  data-testid={`home-guides-region-${first.regionSlug}`}
                >
                  <div className="flex items-center gap-2 text-wine-600 mb-3">
                    <MapPin size={18} weight="fill" />
                    <span className="text-xs font-bold uppercase tracking-widest">
                      {regionGuides.length} guide{regionGuides.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <h3 className="text-xl font-serif text-gray-900 mb-3 group-hover:text-wine-700 transition-colors">
                    {region}
                  </h3>
                  <ul className="space-y-1.5 mb-4">
                    {regionGuides.slice(0, 4).map((g) => (
                      <li key={g.slug} className="text-sm text-gray-600 leading-snug">
                        <span className="text-gray-400 mr-2">›</span>
                        Best {g.intent.toLowerCase()} itineraries
                      </li>
                    ))}
                    {regionGuides.length > 4 && (
                      <li className="text-xs text-gray-500 pl-4">
                        + {regionGuides.length - 4} more…
                      </li>
                    )}
                  </ul>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-wine-700 group-hover:gap-2 transition-all">
                    Explore {region.split(' ')[0]}
                    <ArrowRight size={14} weight="bold" />
                  </span>
                </Link>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-10 md:hidden text-center">
          <Link
            to="/wine-country-guides"
            className="inline-flex items-center gap-2 text-sm font-semibold text-wine-700"
            data-testid="home-guides-view-all-link-mobile"
          >
            View all guides
            <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default RegionalGuidesShowcase;
